import React, { FC, PropsWithChildren, useMemo, useRef } from 'react';
// eslint-disable-next-line no-restricted-imports
import { Transition } from 'react-transition-group';

// Common Dependencies
import { Box, Flex, css } from '@leagueplatform/genesis-commons';
import { usePrefersReducedMotion } from '@leagueplatform/web-common';

// Hooks

// Common Components

// Local Components

// Constants
import {
  TRANSITIONS,
  TRANSITION_DURATION_MS,
} from '../constants/multi-step-footer-constants';

// Types
interface MultiStepSlideProps {
  gap?: string;
  slideIndex: number;
  activeIndex: number;
}

// Styles
const slideTransition = `transform ${TRANSITIONS.PAGE}, opacity ${TRANSITIONS.PAGE}`;
const reducedMotionSlideTransition = `opacity ${TRANSITIONS.PAGE}`;
const reducedMotionSlideStyles = css({
  insetInlineStart: 0,
  transform: 'none',
  transition: reducedMotionSlideTransition,
});

/**
 * This hook determines each slide's transitionable styles based on the users preference for reduced motion
 * @returns `css()` styles
 */
const useSlideTransitionStyles = ({
  slideIndex,
  gap,
  activeIndex,
}: MultiStepSlideProps) => {
  const prefersReducedMotion = usePrefersReducedMotion();

  const slideStyles = css({
    insetInlineStart: `calc(${slideIndex} * ${gap})`,
    transform: `translateX(calc(-${gap} * ${activeIndex}))`,
    transition: slideTransition,
    pointerEvents: activeIndex === slideIndex ? 'auto' : 'none',
  });

  return useMemo(
    () => (prefersReducedMotion ? reducedMotionSlideStyles : slideStyles),
    [prefersReducedMotion, slideStyles],
  );
};

export const MultiStepSlide: FC<PropsWithChildren<MultiStepSlideProps>> = ({
  children,
  gap = '100%',
  slideIndex,
  activeIndex,
}) => {
  const slideTransitionStyles = useSlideTransitionStyles({
    gap,
    slideIndex,
    activeIndex,
  });
  const nodeRef = useRef<HTMLDivElement>(null);

  return (
    <Box
      aria-hidden={activeIndex !== slideIndex}
      css={slideTransitionStyles}
      height="100%"
      opacity={Number(activeIndex === slideIndex)}
      overflowY="auto"
      position="absolute"
      top={0}
      width="100%"
      className="multi-step-slide"
    >
      <Transition
        nodeRef={nodeRef}
        in={activeIndex === slideIndex}
        timeout={TRANSITION_DURATION_MS.XLONG}
        unmountOnExit
      >
        {React.isValidElement(children)
          ? React.cloneElement(children as React.ReactElement, { ref: nodeRef })
          : children}
      </Transition>
    </Box>
  );
};

export const MultiStepSlider: FC<PropsWithChildren<{}>> = ({ children }) => (
  <Flex
    height="100%"
    width="100%"
    position="relative"
    className="multi-step-slider"
  >
    {children}
  </Flex>
);
